import type { JSX } from "react";
import { useState } from "react";

import { CircleSwitcherItem } from "@similarweb/ui-components";

import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";

import {
    type DataFormat,
    dataModeMap,
    numbersDataFormat,
    percentDataFormat,
} from "./ChannelsOverviewBarChartContainerFormat";
import { StyledSwitcher } from "./StyledComponents";

interface Props {
    onClick: (dataFormat: DataFormat) => void;
    dataFormat: DataFormat;
}

enum EdataFormatIndex {
    PERCENT,
    NUMBERS,
}

export const ChannelsOverviewBarChartSwitcher = ({ onClick, dataFormat }: Props): JSX.Element => {
    const selectedIndex = dataFormat === numbersDataFormat ? EdataFormatIndex.NUMBERS : EdataFormatIndex.PERCENT;
    const visibleFormats: DataFormat[] = [percentDataFormat, numbersDataFormat];

    const handleClick = (index: number) => {
        const selectedDataFormat = visibleFormats[index];
        onClick(selectedDataFormat);

        allTrackers.dispatchTrackEvent(
            "Measure Button",
            "click",
            `Bar Chart/Traffic Sources Overview/${dataModeMap[selectedDataFormat]}`,
        );

        const params = {
            channelsOverviewDataFormatType: selectedDataFormat,
        };

        proNavigator.applyUpdateParams(params, { reload: false });
    };

    return (
        <StyledSwitcher
            selectedIndex={selectedIndex}
            customClass="CircleSwitcher"
            onItemClick={(index: number) => handleClick(index)}
        >
            {visibleFormats.map((format) => (
                <CircleSwitcherItem key={format}>{format}</CircleSwitcherItem>
            ))}
        </StyledSwitcher>
    );
};

export const useDataFormat = (isCompare: boolean): [DataFormat, React.Dispatch<React.SetStateAction<DataFormat>>] => {
    const [dataFormat, setDataFormat] = useState<DataFormat>(() => {
        const { channelsOverviewDataFormatType } = proNavigator.getParams();
        if (channelsOverviewDataFormatType && isCompare) {
            return channelsOverviewDataFormatType === numbersDataFormat.toString()
                ? numbersDataFormat
                : percentDataFormat;
        } else {
            return isCompare ? numbersDataFormat : percentDataFormat;
        }
    });

    return [dataFormat, setDataFormat];
};
